import { useEffect, useId, useState } from 'react'

import { FormFieldLabel } from '@/components/ui/form-field-label'
import {
  DURATION_UNITS,
  durationToSeconds,
  splitDuration,
  validateReminderSettings,
  type DurationUnit,
  type ReminderSettingsInput,
} from '@/db/domain/customerReminders'
import { SectionFooter, SectionShell } from '@/screens/SettingsScreen'

export interface ReminderSettingsSectionProps {
  /** The settings in force (admin's choices over the built-in defaults). */
  value: ReminderSettingsInput
  onSave?: (value: ReminderSettingsInput) => Promise<void> | void
  isSaving?: boolean
  errorMessage?: string | null
  successMessage?: string | null
}

interface DurationDraft {
  /** Stable React key for schedule rows (they have no other identity). */
  key: number
  value: string
  unit: DurationUnit
}

let draftKey = 0

function toDraft(seconds: number): DurationDraft {
  const { value, unit } = splitDuration(seconds)
  return { key: ++draftKey, value: String(value), unit }
}

function draftSeconds(draft: DurationDraft): number {
  const n = Number(draft.value)
  return Number.isFinite(n) && n >= 0 ? durationToSeconds(n, draft.unit) : Number.NaN
}

function DurationInput(props: {
  id: string
  label: string
  draft: DurationDraft
  onChange: (draft: DurationDraft) => void
  disabled?: boolean
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <input
        id={props.id}
        aria-label={props.label}
        type="number"
        min={0}
        step="any"
        value={props.draft.value}
        disabled={props.disabled}
        onChange={(event) => props.onChange({ ...props.draft, value: event.target.value })}
        className="h-8 w-20 rounded-md border border-slate-300 px-2 text-[13px]"
      />
      <select
        aria-label={`${props.label} unit`}
        value={props.draft.unit}
        disabled={props.disabled}
        onChange={(event) =>
          props.onChange({ ...props.draft, unit: event.target.value as DurationUnit })
        }
        className="h-8 rounded-md border border-slate-300 bg-white px-1 text-[13px]"
      >
        {DURATION_UNITS.map((unit) => (
          <option key={unit} value={unit}>
            {unit}
          </option>
        ))}
      </select>
    </span>
  )
}

const ordinal = (n: number) => ['1st', '2nd', '3rd'][n] ?? `${n + 1}th`

/**
 * Admin form for customer reminders: when messages go out (schedule) and when
 * to give up (max age). The server follows
 * edits straight away — messages already waiting are rescheduled, not frozen.
 */
export function ReminderSettingsSection(props: ReminderSettingsSectionProps) {
  const { value, onSave, isSaving = false, errorMessage, successMessage } = props
  const [schedule, setSchedule] = useState<DurationDraft[]>(() => value.scheduleSeconds.map(toDraft))
  const [repeat, setRepeat] = useState<DurationDraft>(() => toDraft(value.repeatSeconds))
  const [repeatEnabled, setRepeatEnabled] = useState(value.repeatSeconds > 0)
  const [maxAge, setMaxAge] = useState(String(value.maxAgeDays))
  const [touched, setTouched] = useState(false)
  const ids = useId()

  const reset = () => {
    setSchedule(value.scheduleSeconds.map(toDraft))
    setRepeat(toDraft(value.repeatSeconds))
    setRepeatEnabled(value.repeatSeconds > 0)
    setMaxAge(String(value.maxAgeDays))
    setTouched(false)
  }

  // Follow server-side changes (another admin, realtime) until edited here.
  useEffect(() => {
    if (touched) return
    const v = value
    setSchedule(v.scheduleSeconds.map(toDraft))
    setRepeat(toDraft(v.repeatSeconds))
    setRepeatEnabled(v.repeatSeconds > 0)
    setMaxAge(String(v.maxAgeDays))
  }, [value, touched])

  const edit = <T,>(setter: (next: T) => void) => (next: T) => {
    setTouched(true)
    setter(next)
  }

  const draftValue: ReminderSettingsInput = {
    scheduleSeconds: schedule.map(draftSeconds),
    repeatSeconds: repeatEnabled ? draftSeconds(repeat) : 0,
    maxAgeDays: Number(maxAge),
  }
  const problem = validateReminderSettings(draftValue)
  const dirty = touched
  const canSave = dirty && !problem && !isSaving

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSave) return
        void Promise.resolve(onSave?.(draftValue)).then(() => setTouched(false))
      }}
    >
      <SectionShell
        title="Customer reminders"
        description="When customers with devices ready for collection hear from us. Messages go out as soon as they are due, at any hour; WhatsApp itself decides whether they are actually sent."
      >
        <div className="space-y-2">
          <FormFieldLabel htmlFor={`${ids}-schedule-0`}>Schedule</FormFieldLabel>
          <p className="text-[13px] text-slate-500">
            The 1st message goes out this long after the customer's first device is ready (0 = straight
            away). A job that becomes ready later is announced straight away. Each later message goes
            out this long after the previous one.
          </p>
          <ol className="space-y-2">
            {schedule.map((draft, index) => (
              <li key={draft.key} className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="w-24 text-slate-600">{ordinal(index)} message</span>
                <DurationInput
                  id={`${ids}-schedule-${index}`}
                  label={`${ordinal(index)} message delay`}
                  draft={draft}
                  disabled={isSaving}
                  onChange={(next) =>
                    edit(setSchedule)(schedule.map((d, i) => (i === index ? next : d)))
                  }
                />
                {index === 0 ? <span className="text-slate-400">after the device is ready</span> : <span className="text-slate-400">after the previous one</span>}
                {schedule.length > 1 ? (
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => edit(setSchedule)(schedule.filter((_, i) => i !== index))}
                    className="rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => edit(setSchedule)([...schedule, toDraft(3 * 86_400)])}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Add a message
          </button>

          <div className="flex flex-wrap items-center gap-2 pt-2 text-[13px]">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={repeatEnabled}
                disabled={isSaving}
                onChange={(event) => edit(setRepeatEnabled)(event.target.checked)}
              />
              Then keep reminding every
            </label>
            <DurationInput
              id={`${ids}-repeat`}
              label="Repeat interval"
              draft={repeat}
              disabled={isSaving || !repeatEnabled}
              onChange={edit(setRepeat)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FormFieldLabel htmlFor={`${ids}-max-age`}>Stop after</FormFieldLabel>
          <p className="text-[13px] text-slate-500">
            Give up this many days after the customer's most recent device became ready. The reminder
            stays on the Reminders page as expired; a new device restarts it.
          </p>
          <span className="inline-flex items-center gap-2 text-[13px]">
            <input
              id={`${ids}-max-age`}
              aria-label="Stop after"
              type="number"
              min={1}
              value={maxAge}
              disabled={isSaving}
              onChange={(event) => edit(setMaxAge)(event.target.value)}
              className="h-8 w-20 rounded-md border border-slate-300 px-2 text-[13px]"
            />
            days
          </span>
        </div>

        {dirty && problem ? <p className="text-[13px] text-rose-600">{problem}</p> : null}
        <SectionFooter
          submitLabel="Save reminder settings"
          savingLabel="Saving…"
          canSave={canSave}
          isSaving={isSaving}
          dirty={dirty}
          onDiscard={reset}
          errorMessage={errorMessage}
          successMessage={successMessage}
        />
      </SectionShell>
    </form>
  )
}
